import * as React from 'react';
import cx from 'classnames';
import { DatasheetProps, useDatasheetController } from './datasheet.controller';
import { TextField } from './components/text-field';
import { TableField } from './components/table-field';
import { useDrop } from 'react-dnd';
import { getClosestCoordinateByStep } from './datasheet.utils';
import { DatasheetHolder } from './components/datasheet-holder';
import { FieldType } from '@common/dto/field/type';
import { DatasheetElement, DatasheetElementType } from "@modules/datasheets/types/datasheet-element";
import { TextElement } from './components/text-element';
import { DatasheetFieldComponentGetDto } from '@common/dto/datasheet/field-component/get-dto';
import { DatasheetTextComponentGetDto } from '@common/dto/datasheet/text-component/get-dto';

import './datasheet.styles.scss';

export const Datasheet: React.FC<DatasheetProps> = (props) => {
  const { 
    datasheet,
    datasheetElementDragType, 
    datasheetElements, 
    onDatasheetElementsDrop, 
    onSelectionEnd, 
    isDatasheetElementSelected, 
    onSelectionReset, 
    onDatasheetElementSelect,
    onDatasheetElementUpdate,
    onDatasheetElementResize,
    onDatasheetElementMove,
    className,
    selectedDatasheetElementsCount,
    gridSettingsMargin,
    pageBreaks,
    onTabOrderChange,
    printableDatasheetHolderId,
  } = props;
  const { datasheetReference } = useDatasheetController(props);
  const [{}, dropRef] = useDrop({
    accept: datasheetElementDragType,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset();

      if (offset && datasheetReference.current) {
        const dropTargetXy = datasheetReference.current.getBoundingClientRect();

        onDatasheetElementsDrop({
          firstDatasheetElementCoordinates: {
            x: datasheet.gridSettings.snapToGrid 
              ? getClosestCoordinateByStep(offset.x - dropTargetXy.left, datasheet.gridSettings.horizontalGrid)
              : offset.x - dropTargetXy.left,
            y: datasheet.gridSettings.snapToGrid 
              ? getClosestCoordinateByStep(offset.y - dropTargetXy.top, datasheet.gridSettings.verticalGrid)
              : offset.y - dropTargetXy.top,
          }
        });
      }
    },
  });

  return (
    <div className={cx("DatasheetsEditorDatasheet", className)} ref={dropRef}>
      <DatasheetHolder
        printableDatasheetHolderId={printableDatasheetHolderId}
        selectionDisabled={datasheet.gridSettings.showEditTabOrder}
        className="DatasheetsEditorDatasheet__grid" 
        style={{
          backgroundSize: datasheet.gridSettings.showGrid 
            ? `${datasheet.gridSettings.horizontalGrid}px ${datasheet.gridSettings.verticalGrid}px`
            : undefined,
        }} 
        hasSelectedDatasheetElements={Boolean(selectedDatasheetElementsCount)}
        onSelectionReset={onSelectionReset}
        reference={datasheetReference}
        onSelectionEnd={onSelectionEnd}
      >
        {gridSettingsMargin?.show && (
          <div 
            className="DatasheetsEditorDatasheet__grid__margin"
            style={{
              width: gridSettingsMargin?.width,
              height: gridSettingsMargin?.height,
            }}
          />
        )}
        {pageBreaks.show && (
          <div 
            className="DatasheetsEditorDatasheet__grid__page-breaks" 
            style={{
              backgroundSize: `${pageBreaks.width}px ${pageBreaks.height}px`,
            }}
          />
        )}
        {datasheetElements.map((x) => {
          const resizeHandlerStrategy = selectedDatasheetElementsCount > 1 ? 'immediate' : 'end';
          const multipleElementsSelected = selectedDatasheetElementsCount > 1;

          if (x.type === DatasheetElementType.text) {
            return (
              <TextElement 
                isTabOrderSettingActive={datasheet.gridSettings.showEditTabOrder}
                key={x.id}
                onCoordinatesChange={onDatasheetElementMove}
                multipleElementsSelected={selectedDatasheetElementsCount > 1}
                datasheetGridSettings={datasheet.gridSettings}
                datasheetElement={x as DatasheetElement<DatasheetTextComponentGetDto>} 
                onResize={onDatasheetElementResize}
                onSelect={onDatasheetElementSelect}
                isSelected={isDatasheetElementSelected}
                containerRef={datasheetReference}
                onTextChange={onDatasheetElementUpdate}
                resizeHandlerStrategy={resizeHandlerStrategy}
              />
            );
          }

          const datasheetElementFieldComponent = x as DatasheetElement<DatasheetFieldComponentGetDto>;

          if ([
            FieldType.table
          ].includes(datasheetElementFieldComponent.original.field.type)) {
            return (
              <TableField 
                isTabOrderSettingActive={datasheet.gridSettings.showEditTabOrder}
                key={datasheetElementFieldComponent.id}
                resizeHandlerStrategy={resizeHandlerStrategy}
                multipleElementsSelected={multipleElementsSelected}
                datasheetGridSettings={datasheet.gridSettings}
                datasheetElement={datasheetElementFieldComponent} 
                onResize={onDatasheetElementResize}
                onSelect={onDatasheetElementSelect}
                isSelected={isDatasheetElementSelected}
                containerRef={datasheetReference}
                onCoordinatesChange={onDatasheetElementMove}
              />
            );
          }

          return (
            <TextField 
              isTabOrderSettingActive={datasheet.gridSettings.showEditTabOrder}
              key={datasheetElementFieldComponent.id}
              onTabOrderChange={onTabOrderChange}
              resizeHandlerStrategy={resizeHandlerStrategy}
              multipleElementsSelected={multipleElementsSelected}
              datasheetGridSettings={datasheet.gridSettings}
              datasheetElement={datasheetElementFieldComponent} 
              onResize={onDatasheetElementResize}
              onSelect={onDatasheetElementSelect}
              isSelected={isDatasheetElementSelected}
              containerRef={datasheetReference}
              onCoordinatesChange={onDatasheetElementMove}
            />
          );
        })}
      </DatasheetHolder>
    </div>
  );
};